/* eslint-disable @typescript-eslint/no-unsafe-return */
import BigNumber from 'bignumber.js';
import bs58 from 'bs58';
import crypto from 'crypto';
import {
  AppError,
  ErrorCodes,
  getLocale,
  translate,
  validate,
} from '@ywwwtseng/ywjs';
import * as web3 from '@ywwwtseng/web3';
import * as schema from '@/db/schema';
import { NETWORKS } from '@/constants';
import locales from '@/locales';
import { parseTokenId, getNativeTokenId } from './token';

// export function encode(num: string) {
//   let n = BigInt(num);
//   const bytes = [];
//   const BIG_256 = BigInt(256); // 替代 256n
//   while (n > 0) {
//     bytes.unshift(Number(n % BIG_256));
//     n = n / BIG_256;
//   }
//   return bs58.encode(Buffer.from(bytes));
// }

// export function decode(str: string) {
//   const bytes = bs58.decode(str);
//   let n = BigInt(0); // 替代 0n
//   const BIG_8 = BigInt(8); // 替代 8n
//   for (const b of bytes) {
//     n = (n << BIG_8) + BigInt(b);
//   }
//   return n.toString();
// }

export function encode(num: string) {
  let n = BigInt(num);
  const bytes = [];
  while (n > 0) {
    bytes.unshift(Number(n % 256n));
    n = n / 256n;
  }
  return bs58.encode(Buffer.from(bytes));
}

export function decode(str: string) {
  const bytes = bs58.decode(str);
  let n = 0n;
  for (const b of bytes) {
    n = (n << 8n) + BigInt(b);
  }
  return n.toString();
}

export type AdsGramParam = {
  campaign_id: string;
  banner_id: string;
  publisher_id: string;
  click_id: string;
  record_data: string;
};

export const TMAParam = {
  parse<T extends 'r' | 'n' | 'g' | 's' | 'c' | 'ads'>(
    t: T | T[],
    start_param: string | null | undefined
  ): T extends 'r' | 'g'
    ? string | null
    : T extends 'ads'
    ? AdsGramParam | null
    : T extends 'n' | 's' | 'c'
    ? {
      screen: string;
      params: Record<string, string | number | boolean>;
    } | null
    : null {
    if (!start_param) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return null as any;
    }

    if (Array.isArray(t)) {
      for (const item of t) {
        const result = TMAParam.parse(item, start_param);
        if (result) {
          return result;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return null as any;
    }


    const [type, ...params] = start_param.split('_');

    if (t === 'r' && type === t && params[0]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return decode(params[0]) as any;
    }

    if (t === 'g' && type === t && params[0]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return params[0] as any;
    }

    if (t === 's' && type === t && params[0]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return {
        screen: 'ShareGift',
        params: {
          gift_id: params[0],
        },
      } as any;
    }

    if (t === 'c' && type === t && params[0] && params[1]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return {
        screen: 'ChatGrowthGift',
        params: {
          gift_id: params[0],
          invite_code: params[1],
        },
      } as any;
    }

    if (t === 'ads' && type === t && params.length >= 5) {
      const campaign_id = params[0];
      const banner_id = params[1];
      const publisher_id = params[2];
      const click_id = params[3];
      // record_data 可能包含下划线，需合并第 5 段之后的所有内容
      const record_data = params.slice(4).join('_');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return {
        campaign_id,
        banner_id,
        publisher_id,
        click_id,
        record_data,
      } as any;
    }





    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return null as any;
  },
  format: (t: string, params: string) => {
    return `${t}_${params}`;
  },
};

export function t(
  language_code: string | undefined | null,
  key: string,
  params?: Record<string, string | number>
) {
  const local = getLocale(locales, language_code ?? 'en-US', locales['en-US']);
  return translate(local, key, params) as string;
}

export function medal(index: number) {
  const medals = ['🥇', '🥈', '🥉'];
  return medals[index] ?? '🏅';
}

export function mask(input: string) {
  return input.slice(0, 1) + '*'.repeat(4) + input.slice(-1);
}

export function isNeedVerifyAccount(
  me: typeof schema.users.$inferSelect
): boolean {
  const { error } = validate(me, {
    first_name: { type: 'string' },
    last_name: { type: 'string' },
    birth_date: { type: 'string' },
    email: { type: 'string' },
    gender: { type: 'enum', enum: schema.users.gender.enumValues },
  });


  return !!error;
}

export function getUserWalletAddress(
  network: string,
  me: typeof schema.users.$inferSelect,
) {
  if (network === NETWORKS.SOLANA) {
    return me.sol_address;
  } else if (network === NETWORKS.BSC) {
    return me.bsc_address;
  } else if (network === NETWORKS.ETHEREUM) {
    return me.eth_address;
  } else if (network === NETWORKS.TON) {
    return me.ton_address;
  } else if (network === NETWORKS.TRON) {
    return me.tron_address;
  } else if (network === NETWORKS.BTC) {
    return me.btc_address;
  }

  throw new AppError(
    ErrorCodes.NOT_FOUND,
    `User ${network} wallet address not set`,
    {
      user_id: me.id,
      network,
    }
  );
}

export function isWalletOutOfSync(
  network: string,
  me?: typeof schema.users.$inferSelect,
  address?: string
) {
  if (!me) {
    return false;
  }

  if (network === NETWORKS.SOLANA) {
    return !me.sol_address || me.sol_address !== address;
  } else if (network === NETWORKS.BSC) {
    return !me.bsc_address || me.bsc_address.toLowerCase() !== address?.toLowerCase();
  } else if (network === NETWORKS.ETHEREUM) {
    return !me.eth_address || me.eth_address.toLowerCase() !== address?.toLowerCase();
  } else if (network === NETWORKS.TON) {
    return !me.ton_address || me.ton_address !== address;
  }

  return false;
}

export function getOfficialWalletAddress(network: string) {
  if (network === NETWORKS.SOLANA) {
    return '5g1QJWjSKuP2Pd2hbRffiSKPt7qgNvHgSN3m7nzRNbBM';
  } else if (network === NETWORKS.BSC) {
    return '0x723324C6EB42a8D417d43699D93Ad0Df6DE2479B';
  }

  throw new AppError(
    ErrorCodes.NOT_FOUND,
    `Official ${network} wallet address not set`,
    {
      network,
    }
  );
}

export function getRecommendedNetworkFee({
  network,
}: {
  network: string;
}): string | undefined {
  const { decimals } = parseTokenId(getNativeTokenId(network));
  const recommendedNetworkFee: Record<string, string> = {
    [NETWORKS.SOLANA]: '0.000005', // 0 SOL
    [NETWORKS.ETHEREUM]: '0.00003', // 0.0001 ETH
    [NETWORKS.TON]: '0.05', // 10 TON
    [NETWORKS.TRON]: '0', // 10 TRX
    [NETWORKS.BTC]: '0', // 0.001 BTC
    [NETWORKS.BSC]: '0.000015', // 0.01 BNB
  };

  return recommendedNetworkFee[network]
    ? web3.utils.parseUnits(recommendedNetworkFee[network], decimals).toString()
    : undefined;
}

export function isInsufficientNetworkFee({
  token,
  amount,
  balance,
}: {
  token: typeof schema.tokens.$inferSelect;
  amount: string;
  balance: string;
}) {
  if (!token || !token.network) {
    return false;
  }

  const { decimals } = parseTokenId(getNativeTokenId(token.network));

  const recommendedNetworkFee = getRecommendedNetworkFee({
    network: token.network,
  });

  if (!recommendedNetworkFee) {
    return false;
  }

  if (token && !token.token_address) {
    return BigNumber(balance)
      .minus(web3.utils.parseUnits(amount, decimals))
      .lt(recommendedNetworkFee);
  }

  return BigNumber(balance).lt(recommendedNetworkFee);
}

export function shortenAddress(address: string) {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function generateSignature(
  apiSecret: string,
  body: string | Record<string, unknown>,
  nonce: string,
  timestamp: string
) {
  const payload = JSON.stringify(body) + timestamp + (nonce || '');
  return crypto.createHmac('sha256', apiSecret).update(payload).digest('hex');
}
